'use client'

import {observer} from 'mobx-react'
import React, {useEffect} from 'react'
import {useFormContext} from 'react-hook-form'
import {toast} from 'react-toastify'
import z from 'zod'

import {useBasket} from '@/src/entities/basket'
import {useCertificatesViewModel} from '@/src/entities/product'

import noPhotoPic from '@/src/shared/assets/media/no-photo.png'
import Button from '@/src/shared/ui/button'
import Gallery from '@/src/shared/ui/gallery'
import {Image} from '@/src/shared/ui/image'
import {InputNumber} from '@/src/shared/ui/inputs/input-number'
import {Label} from '@/src/shared/ui/label'
import {getFormErrorKeys, sanitizeHtml} from '@/src/shared/utils/functions'

import * as UI from './certificates.styled'
import ProductDescription from './product-description'
import {CertificatesPageContent} from '@/src/core/domain/entities/catalog'
import {PageData} from '@/src/core/domain/entities/page/page-data'
import {ProductSimple} from '@/src/core/domain/entities/product'

export const CertificatesSchema = z.object({
  certificate_id: z.string({message: 'Сертификат не выбран'}).min(1, 'Сертификат не выбран'),
  certificate_custom_amount: z
    .number()
    .min(1, 'Минимум 1 рубль')
    .max(1000000, 'Максимум 1 000 000 рублей')
    .optional()
    .nullable(),
})

type FormFields = {certificate_id: Nullable<string>; certificate_custom_amount: Nullable<number>}

export const Certificates: React.FC<PageData<CertificatesPageContent>> = observer(() => {
  const basketStore = useBasket()

  const certificatesVM = useCertificatesViewModel()
  const form = useFormContext<FormFields>()

  const certificateCustomAmount = form.watch('certificate_custom_amount')

  const selectFirstCert = (certs: ProductSimple[]) => {
    if (certs.length > 0) {
      form.setValue('certificate_id', certs[0]!.id)
      return certs[0] ?? null
    }
    return null
  }
  useEffect(() => {
    selectFirstCert(certificatesVM.data.certificates)
  }, [])

  const onSubmit = form.handleSubmit(
    async data => {
      await basketStore!.addCertificate(
        data.certificate_id!,
        data.certificate_custom_amount ?? undefined,
      )
    },
    errors => {
      toast.error(
        getFormErrorKeys<FormFields>(errors)
          .map(fieldName => errors[fieldName]?.message)
          .join(',\r\n'),
      )
    },
  )

  return (
    <>
      <UI.Component>
        <h1>Сертификаты</h1>

        <div className="product-card">
          <div className="product-card--main-col">
            <div className=" nofloat m20">
              <UI.GalleryWrapper className="product-image m10" style={{textAlign: 'center'}}>
                {(certificatesVM.selectedCertificate &&
                  certificatesVM.selectedCertificate.images &&
                  certificatesVM.selectedCertificate.images.filter(im => !!im).length > 0 && (
                    <Gallery
                      images={certificatesVM.selectedCertificate.images.filter(im => !!im)}
                    />
                  )) || <Image src={noPhotoPic} width={500} height={500} alt="" />}
              </UI.GalleryWrapper>

              <div className="not-small-mobile">
                <div className="product-card--section description-block">
                  {certificatesVM.selectedCertificate?.description && (
                    <>
                      <div className="product-card--section-caption">Описание</div>

                      <ProductDescription
                        className="description-main"
                        description={certificatesVM.selectedCertificate.description}
                      />
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="product-card--aside">
            <div className="product-sizes-block">
              <div className="js-size-choice-block m40">
                <UI.CertificatesSelectorTitle className="warehouses-label-block s24 bb">
                  Выберите номинал
                </UI.CertificatesSelectorTitle>
                <div className="size-choice-block">
                  {(certificatesVM.data.certificates  ?? []).length > 0 &&
                    certificatesVM.data.certificates.map(certificate => (
                      <UI.CertificateButton
                        as={Button}
                        key={certificate.id}
                        {...((certificatesVM.selectedCertificate?.id === certificate.id && {
                          primary: true,
                        }) || {
                          outlined: true,
                        })}
                        onClick={() => {
                          certificatesVM.selectCertificate(certificate)
                          form.setValue('certificate_id', certificate.id)
                          form.setValue('certificate_custom_amount', null)
                        }}>
                        {(certificate.price && certificate.price.price_formatted) || '<?>'}
                      </UI.CertificateButton>
                    ))}

                  {(certificatesVM.data.custom_certificate && (
                    <UI.CertificateAmount
                      as={InputNumber}
                      name="certificate_amount"
                      placeholder="Другая сумма"
                      value={certificateCustomAmount}
                      min={1}
                      max={1000000}
                      step={1}
                      precision={0}
                      onChange={value => {
                        certificatesVM.selectCertificate(certificatesVM.data.custom_certificate)
                        form.setValue('certificate_id', certificatesVM.data.custom_certificate!.id)
                        form.setValue('certificate_custom_amount', value)
                        if (!value) {
                          const cert = selectFirstCert(certificatesVM.data.certificates)
                          certificatesVM.selectCertificate(cert)
                          form.setValue('certificate_id', cert?.id ?? null)
                        }
                      }}
                    />
                  )) ||
                    ((certificatesVM.data.certificates  ?? []).length == 0 && (
                      <Label>Сертификаты не найдены</Label>
                    ))}
                </div>
              </div>

              <div className="product-card-aside--block not-collapsible">
                <div className="not-small-mobile">
                  <div className="product-old-price-block"></div>
                  <div className="product-price-block m15" style={{display: 'none'}}>
                    <span className="product-price js-certificate-price">0</span> руб.
                  </div>
                </div>

                <UI.SubmitButton
                  as={Button}
                  data-id="2"
                  data-type="certificate"
                  data-key="certificate"
                  onClick={onSubmit}>
                  Добавить в корзину
                </UI.SubmitButton>
              </div>

              <UI.SideIncludeAreas className="product-card-aside--block not-small-mobile">
                {certificatesVM.data.feedback_block && (
                  <UI.ContactUs
                    className="include-area"
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHtml(certificatesVM.data.feedback_block),
                    }}
                  />
                )}
                {certificatesVM.data.delivery_block && (
                  <UI.ShipmentInfo
                    className="include-area"
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHtml(certificatesVM.data.delivery_block),
                    }}
                  />
                )}
              </UI.SideIncludeAreas>
            </div>

            {certificatesVM.selectedCertificate?.description && (
              <ProductDescription
                className="description-aside"
                description={certificatesVM.selectedCertificate.description}
                limited={false}
              />
            )}
          </div>
        </div>
      </UI.Component>
    </>
  )
})
